import { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Grid, Alert, AlertTitle, Button, Divider, Chip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { MemberCreateInvoiceTemplate } from 'src/modules/member/components/memberCreateInvoiceTemplate';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import dayjs from 'dayjs';
import { Email, Phone, Savings, WarningAmber } from '@mui/icons-material';
import { Stack } from '@mui/system';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import MemberInvoiceSelectionDataGrid from './memberInvoicesSelectionDataGrid';
import { MemberInvoiceSelectionSearchFilter } from '../models/memberInvoiceSelectionSearchFilter';

export const MemberInvoiceSelectionComponent = () => {
  const { t } = useTranslation(['common', 'invoice']);
  const isMounted = useIsMounted();
  const { setValue, getValues, watch } = useFormContext();

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [showDataGrid, setShowDataGrid] = useState<boolean>(false);
  const [ownerId, setOwnerId] = useState<number>();
  const { invoiceCountStats, setInvoiceCountStats } = useInvoiceContext();

  // Sätter missionName till fakturamallens namn + dagens datum.
  const invoiceTemplate = watch('invoiceTemplateId');
  const invoiceTemplateId = invoiceTemplate?.template_id || 0;
  useEffect(() => {
    if (invoiceTemplate && invoiceTemplate.name) {
      setValue('missionName', `${invoiceTemplate.name} - ${DateHelper.getDate(new Date())}`);
    }
  }, [invoiceTemplate, setValue]);

  const hasOwnerId = useCallback(() => ownerId !== undefined && ownerId !== 0, [ownerId]);
  const hasInvoiceTemplateId = useCallback(() => invoiceTemplateId !== undefined && invoiceTemplateId !== 0, [invoiceTemplateId]);

  const [memberInvoiceSelectionSearchFilter, setMemberInvoiceSelectionSearchFilter] = useState<MemberInvoiceSelectionSearchFilter>({
    sortCol: 'memberId',
    sortDir: 'desc',
    pageIndex: 0,
    pageSize: 25,
    excludeDate: DateHelper.getFSDate(watch('excludeDate')),
  } as unknown as MemberInvoiceSelectionSearchFilter);

  const searchFilter: MemberInvoiceSelectionSearchFilter = useMemo(() => {
    return memberInvoiceSelectionSearchFilter;
  }, [memberInvoiceSelectionSearchFilter]);

  const updateSearchFilter = () => {
    setMemberInvoiceSelectionSearchFilter((prev) => ({
      ...prev,
      ...{
        excludeDate: DateHelper.getDate(getValues('excludeDate')),
        templateId: invoiceTemplateId,
        pageIndex: 0,
      },
    }));
  };

  const handleChangeTemplateId = (tempId: number | undefined) => {
    setValue('templateId', tempId);
    if (tempId !== invoiceTemplateId) {
      setInvoiceCountStats(undefined);
    }
  };

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberInvoiceSelectionSearchFilter((prev) => ({ ...prev, ...{ filter: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberInvoiceSelectionSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString } }));
      } else {
        setMemberInvoiceSelectionSearchFilter((prev) => ({ ...prev, ...{ filter: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberInvoiceSelectionSearchFilter, tempSearchString]);

  const excludeDateIsInvalid = !watch('excludeDate') || !dayjs(watch('excludeDate')).isValid();
  return (
    <>
      <Box sx={{ p: 2 }}>
        <GridWrapper>
          <Grid item xs={12}>
            <InvoiceGroups
              callback={(ownId) => {
                setOwnerId(ownId);
              }}
              placeholder={t('invoice:createInvoice.selectInvoiceGroup')}
              emptyLabel={t('invoice:createInvoice.selectInvoiceGroup')}
              autoSelect
              ownerId={ownerId}
            />
          </Grid>
          {!hasOwnerId && (
            <Grid item xs={12}>
              <Alert severity='info'>
                <AlertTitle>{t('invoice:createInvoice.selectOwnerIdWarningTitle')}</AlertTitle>
                {t('invoice:createInvoice.selectOwnerIdWarningText')}
              </Alert>
            </Grid>
          )}
          {hasOwnerId() && (
            <>
              <Grid item xs={6}>
                <ControlledDatePicker label={t('Exkludera från')} name='excludeDate' helperText={t('invoice:createMemberInvoice.excludeMembers')} />
              </Grid>

              <Grid item xs>
                <MemberCreateInvoiceTemplate
                  ownerId={ownerId}
                  callback={(val) => {
                    setShowDataGrid(false);
                    handleChangeTemplateId(val);
                  }}
                />
              </Grid>
              <Grid item xs='auto'>
                <Button
                  variant='contained'
                  size='medium'
                  disabled={!watch('templateId') || excludeDateIsInvalid}
                  disableElevation
                  onClick={() => {
                    setShowDataGrid(true);
                    updateSearchFilter();
                  }}
                >
                  {t('invoice:createMemberInvoice.showSelectionButton')}
                </Button>
              </Grid>
              {!watch('templateId') && (
                <Grid item xs={12}>
                  <Alert severity='info'>
                    <AlertTitle>{t('invoice:createMemberInvoice.selectTemplateWarningTitle')}</AlertTitle>
                    {t('invoice:createMemberInvoice.selectTemplateWarningText')}
                  </Alert>
                </Grid>
              )}
            </>
          )}
        </GridWrapper>
      </Box>
      {hasInvoiceTemplateId() && showDataGrid && (
        <>
          {invoiceCountStats && (
            <>
              <Divider />
              <Alert severity='info'>
                <AlertTitle>
                  {t('invoice:createMemberInvoice.totalCountHeader', { count: invoiceCountStats?.totalCount })}
                  {invoiceCountStats?.totalCount > 100 && t('invoice:createMemberInvoice.limitMessage')}
                </AlertTitle>
                <Stack spacing={1} direction='row'>
                  <Chip
                    size='small'
                    color={invoiceCountStats?.emailCount > 0 ? 'success' : 'error'}
                    variant='outlined'
                    sx={{ backgroundColor: '#fff', p: 1, height: '28px', borderRadius: 1 }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {invoiceCountStats?.emailCount > 0 ? <Email sx={{ fontSize: '1rem' }} /> : <WarningAmber sx={{ fontSize: '1rem' }} />}
                        {t('invoice:createMemberInvoice.emailCount', {
                          count: invoiceCountStats?.emailCount,
                          totalCount: invoiceCountStats?.totalCount,
                        })}
                      </Box>
                    }
                  />
                  <Chip
                    size='small'
                    color={invoiceCountStats?.mobilePhoneCount > 0 ? 'success' : 'error'}
                    variant='outlined'
                    sx={{ backgroundColor: '#fff', p: 1, height: '28px', borderRadius: 1 }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {invoiceCountStats?.mobilePhoneCount > 0 ? <Phone sx={{ fontSize: '1rem' }} /> : <WarningAmber sx={{ fontSize: '1rem' }} />}
                        {t('invoice:createMemberInvoice.mobilePhoneCount', {
                          count: invoiceCountStats?.mobilePhoneCount,
                          totalCount: invoiceCountStats?.totalCount,
                        })}
                      </Box>
                    }
                  />
                </Stack>
              </Alert>
              {/* Egen ruta, inte i den blå med urvalets siffror. Den rutan berättar hur urvalet
                  ser ut, den här är ett val användaren gör, och då ska den inte se likadan ut.
                  Visas bara någon i urvalet har tillgodo, och krysset är avbockat från början så
                  att ingen får avdrag utan att ha valt det. */}
              {(invoiceCountStats?.creditTotalAmount ?? 0) > 0 && (
                <Box sx={{ px: 2, pt: 2 }}>
                  <Alert severity='warning' icon={false}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Savings fontSize='small' />
                      <Typography variant='subtitle2'>
                        {t('invoice:createMemberInvoice.creditCount', {
                          count: invoiceCountStats?.creditRecipientCount,
                          totalCount: invoiceCountStats?.totalCount,
                          amount: invoiceCountStats?.creditTotalAmount,
                        })}
                      </Typography>
                    </Box>
                    <ControlledCheckboxNew name='useCredits' label={t('invoice:createMemberInvoice.useCredits')} />
                    <Typography variant='body2' sx={{ ml: 4 }}>
                      {t('invoice:createMemberInvoice.useCreditsHelp')}
                      {(invoiceCountStats?.creditZeroInvoiceCount ?? 0) > 0 &&
                        ` ${t('invoice:createMemberInvoice.useCreditsZeroInvoices', { count: invoiceCountStats?.creditZeroInvoiceCount })}`}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </>
          )}
          <Divider />
          <Box sx={{ position: 'relative', pt: 2.5 }}>
            <Box sx={{ position: 'absolute', top: (thm) => thm.spacing(2), left: (thm) => thm.spacing(2), zIndex: (thm) => thm.zIndex.tooltip }}>
              <SearchField
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            </Box>
            <MemberInvoiceSelectionDataGrid
              searchFilterCallback={(newFilter) =>
                setMemberInvoiceSelectionSearchFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
              searchFilter={searchFilter}
            />
          </Box>
        </>
      )}
    </>
  );
};
